/**
 * Setlist prev/next for the song page: the collection's ordered entries (['collection', id],
 * persisted and seeded from the ['collections'] list, so it works offline; polled every
 * SETLIST_REFRESH_MS while a song of the list is open, so a new shared order reaches every singer
 * within about a minute — TanStack pauses it in the background and offline), history-replacing
 * navigation (Back still returns to the setlist), and a prefetch of the next song — its details
 * (['song', id], same fetcher as the page) and its default sheet's 's' bundle through the bundle
 * source (served from an offline pack / the recent cache when stored, else teed into it).
 */
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { roleAtLeast } from '../../shared/audience';
import type { SheetTier, SongDTO, SongFileDTO } from '../../shared/dto/library';
import { useCollection } from '../api/library';
import { filesForViewer, isViewableSheet, pickDefaultFile, type Viewer } from './fileChoice';
import { orderedEntries, setlistNav, setlistSearch, type SetlistContext, type SetlistNavState, type SetlistStep } from './setlist';
import { openBundle } from './sources';
import { PREF, readPref } from './storage';
import { fetchSong, songKey } from './useSong';

export interface SetlistNavigation extends SetlistNavState {
  collectionId: string;
  /** Collection name (for the control's accessible label). */
  name: string;
  /** Opens another song of the list, replacing the history entry. */
  go: (step: SetlistStep) => void;
}

/** How often the open song's list is re-fetched (the shared order may change). */
export const SETLIST_REFRESH_MS = 60_000;

/** Prev/next state of the open song in its setlist; null without context or with < 2 songs. */
export function useSetlistNav(setlist: SetlistContext | null, songId: string | undefined): SetlistNavigation | null {
  const col = useCollection(setlist?.collectionId, { refetchInterval: SETLIST_REFRESH_MS });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const data = setlist && col.data?.id === setlist.collectionId ? col.data : null;
  const state = useMemo(
    () => (data && songId && setlist ? setlistNav(orderedEntries(data.entries), songId, setlist.index, setlist.entryId) : null),
    [data, songId, setlist],
  );
  const collectionId = setlist?.collectionId ?? '';
  const go = useCallback(
    (step: SetlistStep) => {
      if (!collectionId) return;
      navigate(
        { pathname: `/songs/${encodeURIComponent(step.songId)}`, search: setlistSearch(params, collectionId, step.index, step.entryId) },
        { replace: true },
      );
    },
    [navigate, params, collectionId],
  );
  return useMemo(() => (state && data ? { ...state, collectionId, name: data.name, go } : null), [state, data, collectionId, go]);
}

const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isStr = (v: unknown): v is string | null => v === null || typeof v === 'string';

/** The sheet the song page would open first for this user (same rules as SongScreen). */
export function defaultSheetFor(song: SongDTO, viewer: Viewer): SongFileDTO | null {
  const allFiles = roleAtLeast(viewer.role, 'editor') && readPref(PREF.allFiles, false, isBool);
  const sheets = filesForViewer(song.files, 'sheet', viewer, allFiles, isViewableSheet);
  return pickDefaultFile(sheets, viewer.section, readPref<string | null>(PREF.lastSheet(song.id), null, isStr));
}

/**
 * Reads a bundle through the viewer's bundle source to the end and discards it: a stored copy is
 * merely opened, a network copy lands in the recent LRU (via the offline source's tee) and the
 * HTTP cache. Never throws.
 */
export async function warmBundle(fileId: string, rev: number, tier: SheetTier, signal: AbortSignal): Promise<void> {
  try {
    const src = await openBundle(fileId, rev, tier, signal);
    if (src instanceof Blob || !src.body) return;
    const reader = src.body.getReader();
    try {
      for (;;) {
        if (signal.aborted) {
          await reader.cancel().catch(() => undefined);
          return;
        }
        const { done } = await reader.read();
        if (done) return;
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* already released */
      }
    }
  } catch {
    /* offline, aborted or refused: the song simply loads when opened */
  }
}

/** Fetches the song details (if stale) and warms its default sheet's 's' bundle. Never throws. */
export async function prefetchSongForViewer(qc: QueryClient, songId: string, viewer: Viewer, signal: AbortSignal): Promise<void> {
  await qc.prefetchQuery({ queryKey: songKey(songId), queryFn: () => fetchSong(songId), meta: { persist: true } });
  if (signal.aborted) return;
  const song = qc.getQueryData<SongDTO>(songKey(songId));
  const sheet = song ? defaultSheetFor(song, viewer) : null;
  if (!sheet?.tiers?.s || sheet.rev < 1) return;
  await warmBundle(sheet.id, sheet.rev, 's', signal);
}

/** Wait a little so the open song's own bundles get the bandwidth first. */
export const PREFETCH_DELAY_MS = 3000;

/** Prefetches `songId` (the next song of the setlist) a few seconds after the page settles. */
export function usePrefetchSong(songId: string | null, viewer: Viewer): void {
  const qc = useQueryClient();
  const { role, section } = viewer;
  useEffect(() => {
    if (!songId) return;
    const ctrl = new AbortController();
    const timer = setTimeout(() => void prefetchSongForViewer(qc, songId, { role, section }, ctrl.signal), PREFETCH_DELAY_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [qc, songId, role, section]);
}
